import { useMemo, useState } from "react";
import SectionHeader from "../components/SectionHeader";
import CTASection from "../components/CTASection";
import { GALLERY_ITEMS, GALLERY_CATEGORIES } from "../lib/business";
import { placeholderSvg } from "../lib/placeholder";

export default function GalleryPage() {
  const [active, setActive] = useState("All");
  const items = useMemo(
    () => (active === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.category === active)),
    [active]
  );

  return (
    <div data-testid="gallery-page">
      <section className="bg-secondary/60 border-b border-border">
        <div className="container-x py-14 sm:py-20">
          <SectionHeader
            eyebrow="Gallery"
            title="Recent Projects"
            subtitle="Real project photos from local handyman and home service jobs."
          />
          <p className="mt-4 text-xs text-muted-foreground italic max-w-xl">
            Some images below are polished placeholders that will be replaced with real project photos.
          </p>
        </div>
      </section>

      <section className="py-10 border-b border-border bg-white sticky top-16 sm:top-20 z-20">
        <div className="container-x">
          <div
            className="flex flex-wrap gap-2"
            data-testid="gallery-filter-bar"
            role="tablist"
            aria-label="Filter gallery by category"
          >
            {GALLERY_CATEGORIES.map((c) => {
              const isActive = active === c;
              return (
                <button
                  key={c}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(c)}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold border transition-colors ${
                    isActive
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-primary border-border hover:bg-secondary"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x">
          {items.length === 0 ? (
            <p data-testid="gallery-empty" className="text-center text-muted-foreground py-16">
              No photos yet in this category — check back soon.
            </p>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
              {items.map((g, i) => (
                <figure
                  key={`${g.category}-${i}`}
                  data-testid={`gallery-item-${i}`}
                  className="mb-4 break-inside-avoid overflow-hidden rounded-2xl group relative bg-secondary"
                >
                  <img
                    src={g.image}
                    alt={g.label}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholderSvg(g.category); }}
                    className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 inline-flex rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
                    {g.category}
                  </span>
                  <figcaption className="absolute inset-x-0 bottom-0 p-4 text-white text-sm font-semibold bg-gradient-to-t from-primary/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    {g.label}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>
      <CTASection />
    </div>
  );
}
